/**
 * Algolia A/B Test Manager
 * 
 * Manages A/B test assignments for search personalization experiments.
 * Provides 50/50 split testing with full session continuity across pagination,
 * filtering, and sorting.
 * 
 * Features:
 * - Persistent test group assignment (sessionStorage)
 * - analyticsTags injection for Algolia search requests
 * - Session continuity across search interactions
 * - Integration with Algolia Insights API and Adobe CJA
 * 
 * @module AlgoliaABTestManager
 */
(function () {
  'use strict';

  // Constants
  const TEST_ASSIGNMENT_KEY = '_mgn_algolia_test_assignment';
  const TEST_SESSION_KEY = '_mgn_algolia_test_session';
  const SESSION_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes
  
  /**
   * A/B Test Configuration
   * Can be overridden via window.__ALGOLIA_AB_TEST_CONFIG
   */
  const DEFAULT_CONFIG = {
    enabled: true,
    testName: 'search_personalization_experiment',
    variants: {
      control: {
        name: 'baseline',
        weight: 0.5, // 50%
        tags: ['baseline', 'search']
      },
      treatment: {
        name: 'personalized',
        weight: 0.5, // 50%
        tags: ['personalized', 'search']
      }
    },
    // Optional: External bucketing service URL
    externalBucketing: null,
    // Optional: Force assignment for testing (overrides random)
    forceVariant: null // 'control' | 'treatment'
  };

  /**
   * A/B Test Manager Class
   */
  class ABTestManager {
    constructor(config = {}) {
      this.config = { ...DEFAULT_CONFIG, ...config };
      this.testAssignment = null;
      this.sessionId = null;
      
      this.init();
    }

    /**
     * Initialize test manager
     */
    init() {
      if (!this.config.enabled) {
        return;
      }

      // Load or create test session
      this.loadOrCreateSession();
      
      // Get or assign test variant
      this.testAssignment = this.getOrAssignVariant();
      
    }

    /**
     * Load existing session or create new one
     */
    loadOrCreateSession() {
      try {
        const stored = sessionStorage.getItem(TEST_SESSION_KEY);
        if (stored) {
          const session = JSON.parse(stored);
          // Check if session is still valid (not expired)
          if (Date.now() - session.timestamp < SESSION_TIMEOUT_MS) {
            this.sessionId = session.id;
            return;
          }
        }
      } catch (e) {
        console.warn('[AB Test Manager] Failed to load session:', e);
      }

      // Create new session
      this.sessionId = this.generateSessionId();
      this.storeSession();
    }

    /**
     * Store session in sessionStorage
     */
    storeSession() {
      try {
        sessionStorage.setItem(TEST_SESSION_KEY, JSON.stringify({
          id: this.sessionId,
          timestamp: Date.now()
        }));
      } catch (e) {
        console.warn('[AB Test Manager] Failed to store session:', e);
      }
    }

    /**
     * Generate unique session ID
     */
    generateSessionId() {
      return 'exp_' + Date.now() + '_' + Math.random().toString(36).substring(2, 11);
    }

    /**
     * Get or assign test variant for current user
     * @returns {Object} Test assignment with variant and tags
     */
    getOrAssignVariant() {
      // Check for forced variant (testing/debugging)
      if (this.config.forceVariant) {
        return this.createAssignment(this.config.forceVariant);
      }

      // Try to load existing assignment from sessionStorage
      const stored = this.loadStoredAssignment();
      if (stored && this.isValidAssignment(stored)) {
        return stored;
      }

      // Check for external bucketing service
      if (this.config.externalBucketing) {
        // External bucketing handled asynchronously
        // For now, fall back to random assignment
        console.warn('[AB Test Manager] External bucketing not yet implemented');
      }

      // Assign random variant
      const variant = this.assignRandomVariant();
      const assignment = this.createAssignment(variant);
      
      // Store assignment
      this.storeAssignment(assignment);
      
      return assignment;
    }

    /**
     * Load stored test assignment from sessionStorage
     * @returns {Object|null} Stored assignment or null
     */
    loadStoredAssignment() {
      try {
        const stored = sessionStorage.getItem(TEST_ASSIGNMENT_KEY);
        if (stored) {
          return JSON.parse(stored);
        }
      } catch (e) {
        console.warn('[AB Test Manager] Failed to load stored assignment:', e);
      }
      return null;
    }

    /**
     * Validate stored assignment matches current test config
     * @param {Object} assignment - Stored assignment
     * @returns {boolean} Whether assignment is valid
     */
    isValidAssignment(assignment) {
      return (
        assignment &&
        assignment.testName === this.config.testName &&
        assignment.sessionId === this.sessionId &&
        (assignment.variant === 'control' || assignment.variant === 'treatment')
      );
    }

    /**
     * Assign random variant based on configured weights
     * @returns {string} 'control' | 'treatment'
     */
    assignRandomVariant() {
      const rand = Math.random();
      const controlWeight = this.config.variants.control.weight;
      
      return rand < controlWeight ? 'control' : 'treatment';
    }

    /**
     * Create assignment object
     * @param {string} variant - 'control' | 'treatment'
     * @returns {Object} Assignment object
     */
    createAssignment(variant) {
      const variantConfig = this.config.variants[variant];
      
      return {
        testName: this.config.testName,
        variant: variant,
        variantName: variantConfig.name,
        tags: variantConfig.tags,
        sessionId: this.sessionId,
        timestamp: Date.now()
      };
    }

    /**
     * Store assignment in sessionStorage
     * @param {Object} assignment - Assignment to store
     */
    storeAssignment(assignment) {
      try {
        sessionStorage.setItem(TEST_ASSIGNMENT_KEY, JSON.stringify(assignment));
      } catch (e) {
        console.warn('[AB Test Manager] Failed to store assignment:', e);
      }
    }

    /**
     * Get analytics tags for current assignment
     * @returns {string[]} Array of tags for Algolia search request
     */
    getAnalyticsTags() {
      if (!this.config.enabled || !this.testAssignment) {
        return [];
      }
      return this.testAssignment.tags;
    }

    /**
     * Get test variant
     * @returns {string} 'control' | 'treatment' | null
     */
    getVariant() {
      return this.testAssignment ? this.testAssignment.variant : null;
    }

    /**
     * Get test variant name
     * @returns {string} 'baseline' | 'personalized' | null
     */
    getVariantName() {
      return this.testAssignment ? this.testAssignment.variantName : null;
    }

    /**
     * Check if user is in treatment (personalized) group
     * @returns {boolean}
     */
    isPersonalized() {
      return this.getVariant() === 'treatment';
    }

    /**
     * Get test context for analytics events
     * @returns {Object} Test context
     */
    getTestContext() {
      if (!this.testAssignment) {
        return null;
      }

      return {
        testName: this.testAssignment.testName,
        variant: this.testAssignment.variant,
        variantName: this.testAssignment.variantName,
        sessionId: this.sessionId
      };
    }

    /**
     * Reset test assignment (for testing/debugging)
     */
    reset() {
      try {
        sessionStorage.removeItem(TEST_ASSIGNMENT_KEY);
        sessionStorage.removeItem(TEST_SESSION_KEY);
        this.sessionId = null;
        this.testAssignment = null;
      } catch (e) {
        console.warn('[AB Test Manager] Failed to reset:', e);
      }
    }
  }

  // Initialize global instance
  const customConfig = window.__ALGOLIA_AB_TEST_CONFIG || {};
  const manager = new ABTestManager(customConfig);

  // Expose public API
  window.AlgoliaABTestManager = {
    getAnalyticsTags: () => manager.getAnalyticsTags(),
    getVariant: () => manager.getVariant(),
    getVariantName: () => manager.getVariantName(),
    isPersonalized: () => manager.isPersonalized(),
    getTestContext: () => manager.getTestContext(),
    reset: () => manager.reset(),
    // Internal access for advanced use cases
    _instance: manager
  };

})();
